<template>
  <div>
    <!-- Interview Adding Sidebar -->
    <interview-add
      :is-add-new-interview-sidebar-active.sync="isAddNewInterviewSidebarActive"
      :candidate.sync="interviewPayload.candidate"
      :candidate-change-flag.sync="interviewPayload.candidateChangeFlag"
      @refetch-data="refetchCandidatesData"
    />

    <!-- Filters -->
    <app-collapse hover>
      <app-collapse-item title="Filters">
        <candidate-list-filters
          :status-filter.sync="statusFilter"
          :status-options="statusOptions"
          :created-by-filter.sync="createdByFilter"
          :employee-options="interviewersData"
          :referred-by-filter.sync="referredByFilter"
          :referred-by-options="interviewersData"
          :recruiter-by-filter.sync="recruiterByFilter"
          :recruiter-by-options="interviewersData"
          v-bind:source-by-filter.sync="sourceByFilter"
        />
      </app-collapse-item>
    </app-collapse>
    <!-- Table Container Card -->
    <b-card no-body class="mb-0 mt-2">
      <div class="m-2">
        <!-- Table Top -->
        <b-row>
          <!-- Per Page -->
          <b-col
            cols="12"
            md="6"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
          >
            <b-form-checkbox
              v-if="candidatesMetaData.length && $can('candidate_delete')"
              class="mr-2"
              v-model="isDeleteMultipleCandChecked"
            >
            </b-form-checkbox>
            <label>Show</label>
            <v-select
              v-model="perPage"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="perPageOptions"
              :clearable="false"
              :searchable="false"
              class="per-page-selector d-inline-block mx-50"
            />
            <label>entries</label>
          </b-col>

          <!-- Search -->
          <b-col cols="12" md="6">
            <div
              v-if="selectedCandidates.length == 0"
              class="d-flex align-items-center justify-content-end"
            >
              <b-input-group class="input-group-merge">
                <b-input-group-prepend is-text>
                  <feather-icon icon="SearchIcon" />
                </b-input-group-prepend>
                <b-form-input
                  v-model="searchQuery"
                  class="d-inline-block mr-1"
                  placeholder="Search Name/Email/Referred By"
                />
              </b-input-group>
              <b-button
                v-if="$can('candidate_create')"
                variant="primary"
                :to="{ name: 'add-candidate' }"
              >
                <span class="text-nowrap">Add Candidate</span>
              </b-button>
            </div>
            <b-button
              v-else-if="$can('candidate_delete')"
              class="float-right"
              variant="danger"
              :disabled="candidatesDeleteCount == 0"
              @click="confirmDeleteMultipleRecords"
            >
              <span class="text-nowrap"
                >Delete {{ candidatesDeleteCount }} Candidates</span
              >
            </b-button>
          </b-col>
        </b-row>
      </div>

      <b-form-checkbox-group
        id="checkbox-group"
        v-model="selectedCandidates"
        name="selectedCandidates"
      >
        <b-table
          ref="refUserListTable"
          class="position-relative"
          :items="fetchCandidateList"
          responsive
          :fields="tableColumns"
          primary-key="id"
          :sort-by.sync="sortBy"
          show-empty
          empty-text="No matching records found"
          :sort-desc.sync="isSortDirDesc"
        >
          <template #cell(select)="data">
            <div class="text-nowrap">
              <b-form-checkbox :value="data.item.hashid"> </b-form-checkbox>
            </div>
          </template>
          <!-- Column: User -->
          <template #cell(name)="data">
            <b-media vertical-align="center">
              <b-link
                v-if="$can('candidate_show')"
                :to="{
                  name: 'view-candidate',
                  params: { id: data.item.hashid },
                }"
                class="font-weight-bold d-inline-block text-nowrap"
                v-b-tooltip.hover.v-default
                :title="data.item.name"
              >
                {{ data.item.name | str_limit(15) }}
              </b-link>
              <span v-else v-b-tooltip.hover.v-default :title="data.item.name">
                {{ data.item.name | str_limit(15) }}
              </span>
            </b-media>
          </template>

          <!-- Column: User -->
          <template #cell(email)="data">
            <span v-b-tooltip.hover.v-default :title="data.item.email">
              {{ data.item.email | str_limit(15) }}
            </span>
          </template>

          <!-- Column: User -->
          <template #cell(referred_by_name)="data">
            <span
              v-b-tooltip.hover.v-default
              :title="data.item.referred_by_name"
            >
              {{ data.item.referred_by_name | str_limit(15) }}
            </span>
          </template>

          <template #cell(status_label)="data">
            <span
              :style="{
                color:
                  data.item.status_label == 'Selected'
                    ? 'green'
                    : data.item.status_label == 'Rejected'
                    ? 'red'
                    : 'grey',
              }"
              >{{ data.item.status_label }}</span
            >
          </template>

          <!-- Column: Actions -->
          <template #cell(actions)="data">
            <feather-icon
              @click="editRecord(data)"
              v-if="$can('candidate_edit')"
              v-b-tooltip.hover
              title="Edit"
              icon="EditIcon"
              size="16"
              class="mr-1 cursor-pointer"
            />

            <feather-icon
              @click="viewRecord(data)"
              v-if="$can('candidate_show')"
              v-b-tooltip.hover
              title="View"
              icon="EyeIcon"
              size="16"
              class="cursor-pointer"
            />

            <b-dropdown
              variant="link"
              size="sm"
              toggle-class="text-decoration-none"
              no-caret
            >
              <template v-slot:button-content>
                <feather-icon
                  icon="MoreVerticalIcon"
                  size="16"
                  class="text-body align-middle mr-25"
                />
              </template>

              <b-dropdown-item
                class="btn-sm p-0"
                @click="assignInterview(data.item)"
                v-if="$can('interview_create')"
              >
                <feather-icon icon="UserPlusIcon" size="13" class="mr-50" />
                <span>Add Interview</span>
              </b-dropdown-item>

              <b-dropdown-item
                class="btn-sm p-0"
                @click.stop.prevent="openWindow(data.item.resume_file_path)"
                v-if="data.item.resume_file_path != ''"
              >
                <feather-icon icon="FileTextIcon" size="13" class="mr-50" />
                <span>View Resume</span>
              </b-dropdown-item>

              <b-dropdown-item
                class="btn-sm p-0"
                @click="confirmDeleteRecord(data)"
                v-if="$can('candidate_delete')"
              >
                <feather-icon icon="TrashIcon" size="13" class="mr-50" />
                <span>Delete</span>
              </b-dropdown-item>
            </b-dropdown>
          </template>
        </b-table>
      </b-form-checkbox-group>
      <div class="mx-2 mb-2">
        <b-row>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-start"
          >
            <span class="text-muted"
              >Showing {{ dataMeta.from }} to {{ dataMeta.to }} of
              {{ dataMeta.of }} entries</span
            >
          </b-col>
          <!-- Pagination -->
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-end"
          >
            <b-pagination
              v-if="selectedCandidates.length == 0"
              v-model="currentPage"
              :total-rows="totalCandidateList"
              :per-page="perPage"
              first-number
              last-number
              class="mb-0 mt-1 mt-sm-0"
              prev-class="prev-item"
              next-class="next-item"
            >
              <template #prev-text>
                <feather-icon icon="ChevronLeftIcon" size="18" />
              </template>
              <template #next-text>
                <feather-icon icon="ChevronRightIcon" size="18" />
              </template>
            </b-pagination>
            <b-button
              v-else-if="$can('candidate_delete')"
              class="float-right"
              variant="danger"
              :disabled="candidatesDeleteCount == 0"
              @click="confirmDeleteMultipleRecords"
            >
              <span class="text-nowrap"
                >Delete {{ candidatesDeleteCount }} Candidates</span
              >
            </b-button>
          </b-col>
        </b-row>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BButton,
  BCard,
  BCol,
  BFormInput,
  BLink,
  BMedia,
  BPagination,
  BRow,
  BTable,
  VBTooltip,
  BFormCheckbox,
  BFormCheckboxGroup,
} from "bootstrap-vue";
import vSelect from "vue-select";
import store from "@/store";
import { onUnmounted, watch, ref } from "@vue/composition-api";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import AppCollapse from "@core/components/app-collapse/AppCollapse.vue";
import AppCollapseItem from "@core/components/app-collapse/AppCollapseItem.vue";
import candidateStoreModule from "../candidateStoreModule";
import useCandidateList from "./useCandidateList";
import CandidateListFilters from "./CandidateListFilters.vue";
import InterviewAdd from "../../interviews/list/InterviewAdd.vue";

export default {
  components: {
    CandidateListFilters,
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BMedia,
    BLink,
    BPagination,
    AppCollapse,
    AppCollapseItem,
    InterviewAdd,
    vSelect,
    /* eslint-disable */
    ToastificationContent,
    /* eslint-enable */
    BFormCheckbox,
    BFormCheckboxGroup,
  },
  directives: {
    "b-tooltip": VBTooltip,
  },
  setup() {
    const REFERRAL_APP_STORE_MODULE_NAME = "app-candidates";
    const isDeleteMultipleCandChecked = ref(false);
    const selectedCandidates = ref([]);
    const candidatesDeleteCount = ref(0);

    // Register module
    if (!store.hasModule(REFERRAL_APP_STORE_MODULE_NAME)) {
      store.registerModule(
        REFERRAL_APP_STORE_MODULE_NAME,
        candidateStoreModule
      );
    }

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(REFERRAL_APP_STORE_MODULE_NAME))
        store.unregisterModule(REFERRAL_APP_STORE_MODULE_NAME);
    });

    const statusOptions = [
      { label: "In Progress", code: 0 },
      { label: "Selected", code: 1 },
      { label: "Rejected", code: 2 },
    ];

    const {
      fetchCandidateList,
      tableColumns,
      perPage,
      currentPage,
      totalCandidateList,
      candidatesMetaData,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refUserListTable,
      refetchData,
      statusFilter,
      createdByFilter,
      referredByFilter,
      recruiterByFilter,
      sourceByFilter,
    } = useCandidateList();

    watch(isDeleteMultipleCandChecked, (val) => {
      selectedCandidates.value = [];
      if (val) {
        candidatesMetaData.value.forEach((candidate) => {
          selectedCandidates.value.push(candidate.hashid);
        });
      }
    });

    watch(selectedCandidates, (val) => {
      candidatesDeleteCount.value = val.length;
    });

    return {
      // Sidebar
      fetchCandidateList,
      tableColumns,
      perPage,
      currentPage,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refUserListTable,
      refetchData,
      totalCandidateList,
      // Extra Filters
      statusOptions,
      statusFilter,
      createdByFilter,
      candidatesMetaData,
      referredByFilter,
      recruiterByFilter,
      sourceByFilter,
      selectedCandidates,
      candidatesDeleteCount,
      isDeleteMultipleCandChecked,
    };
  },
  data() {
    return {
      isAddNewInterviewSidebarActive: false,
      interviewPayload: {
        candidate: {},
        candidateChangeFlag: true,
      },
      interviewersData: [],
    };
  },
  created() {
    if (!this.$can("candidate_delete")) {
      this.tableColumns = this.tableColumns.filter(
        (item) => item.key != "select"
      );
    }
    this.fetchInterviewersList();
  },
  methods: {
    assignInterview(item) {
      this.interviewPayload.candidate.name = item.name;
      this.interviewPayload.candidate.id = item.hashid;
      this.isAddNewInterviewSidebarActive = true;
    },
    editRecord(data) {
      this.$router.push(`/edit/candidate/${data.item.hashid}`).catch(() => {});
    },
    viewRecord(data) {
      this.$router.push(`/view/candidate/${data.item.hashid}`).catch(() => {});
    },
    confirmDeleteRecord(data) {
      this.$swal({
        title: "Are you sure?",
        text: `You want to delete ${data.item.name}`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, delete it!",
        customClass: {
          confirmButton: "btn btn-primary",
          cancelButton: "btn btn-outline-danger ml-1",
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.value) {
          this.deleteRecord(data.item.hashid);
        }
      });
    },
    confirmDeleteMultipleRecords() {
      this.$swal({
        title: "Are you sure?",
        text: `You want to delete ${this.selectedCandidates.length} Candidates, This cannot be undone`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, delete it!",
        customClass: {
          confirmButton: "btn btn-primary",
          cancelButton: "btn btn-outline-danger ml-1",
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.value) {
          this.$store
            .dispatch(
              "app-candidates/removeMultipleCandidates",
              this.selectedCandidates
            )
            .then((res) => {
              this.isDeleteMultipleCandChecked = false;
              this.selectedCandidates = [];
              if (res.data.success) {
                this.refetchData();
                // this.$toast({
                //   component: ToastificationContent,
                //   props: {
                //     title: "Deleted Successfully",
                //     icon: "BellIcon",
                //     variant: "success",
                //     text: res.data.message,
                //   },
                // });

                this.$toast({
                  component: ToastificationContent,
                  props: {
                    title: 'Deleted Successfully',
                    icon: 'BellIcon',
                    variant: 'success',
                    text: res.data.message,
                  },
                }, {
                  timeout: 2000, 
                  position: 'bottom-center',
                  toastClassName:"radius-8 successful-info",
                  hideProgressBar : true,
                });
              } else {
                // this.$toast({
                //   component: ToastificationContent,
                //   props: {
                //     title: "Candidate Deletion failed",
                //     icon: "BellIcon",
                //     variant: "danger",
                //     text: res.data.message,
                //   },
                // });

                this.$toast({
                  component: ToastificationContent,
                  props: {
                    title: 'Candidate Deletion failed',
                    icon: 'BellIcon',
                    variant: 'danger',
                    text: res.data.message,
                  },
                }, {
                  timeout: 2000, 
                  position: 'bottom-center',
                  toastClassName:"radius-8 error-info ",
                  hideProgressBar : true,
                });
              }
            })
            .catch((err) => console.log(err));
        }
      });
    },
    refetchCandidatesData() {
      console.log("Added Interview");
    },
    deleteRecord(id) {
      const self = this;
      this.$store
        .dispatch("app-candidates/removeCandidate", id)
        .then((res) => {
          if (res.data.success) {
            self.refetchData();
            // self.$toast({
            //   component: ToastificationContent,
            //   props: {
            //     title: "Candidate Deleted",
            //     icon: "BellIcon",
            //     variant: "success",
            //     text: "Candidate deleted successfully",
            //   },
            // });
            this.$toast({
                  component: ToastificationContent,
                  props: {
                    title: 'Candidate Deleted',
                    icon: 'BellIcon',
                    variant: 'success',
                    text: 'Candidate deleted successfully',
                  },
                }, {
                  timeout: 2000, 
                  position: 'bottom-center',
                  toastClassName:"radius-8 successful-info",
                  hideProgressBar : true,
                });
          } else {
            // self.$toast({
            //   component: ToastificationContent,
            //   props: {
            //     title: "Candidate Deletion failed",
            //     icon: "BellIcon",
            //     variant: "danger",
            //     text: res.data.message,
            //   },
            // });

            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Candidate Deletion failed',
                icon: 'BellIcon',
                variant: 'danger',
                text: res.data.message,
              },
            }, {
              timeout: 2000, 
              position: 'bottom-center',
              toastClassName:"radius-8 error-info ",
              hideProgressBar : true,
            });
          }
        })
        .catch((err) => {
          console.error(err);
        });
    },
    openWindow(link) {
      window.open(link);
    },

    fetchInterviewersList() {
      this.$store
        .dispatch("app/fetchInterviewers")
        .then((res) => {
          this.interviewersData = res.data.data;
        })
        .catch((err) => {
          console.error(err);
        });
    },
  },
  watch: {
  '$route.query': function(val) {
    const filter = val.q; // Access the 'q' parameter directly from val
    if (filter) {
      this.searchQuery = filter; // Update the searchQuery data property
    }
  }
},
};
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
